import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';

import CONSTANTS from 'src/store/constants';
import { Presentation } from 'src/components';
import { Page } from 'src/containers';
import { storyActions } from 'src/store/actions';

const storiesFilter = ({ stories }) => stories;

const currentSlideFilter = ({ currentSlide }) => currentSlide;

const hasNextSlideSelector = createSelector(
  [storiesFilter, currentSlideFilter],
  (stories, currentSlide) => stories.length > 0 && currentSlide < stories[0].slides.length - 1
);

const hasPrevSlideSelector = createSelector([currentSlideFilter], (currentSlide) => currentSlide > 0);

function Story({
  history,
  match: {
    params: { datasetId: datasetIdParam },
  },
  app: { tenant },
  story: { segments, stories, currentSlide, fetchStoriesStatus },
  fetchSegments,
  fetchStories,
  hasNextSlide,
  hasPrevSlide,
  nextSlide,
  prevSlide,
}) {
  const getSegmentsData = useCallback(
    (datasetId) => {
      const query = {
        filters: [
          {
            and: [{ name: 'dataset_id', op: 'eq', val: datasetId }],
          },
        ],
      };
      fetchSegments(query);
    },
    [fetchSegments]
  );

  const getStoriesData = useCallback(
    (datasetId) => {
      const query = {
        filters: [
          {
            and: [{ name: 'dataset_id', op: 'eq', val: datasetId }],
          },
        ],
      };
      fetchStories(query);
    },
    [fetchStories]
  );

  useEffect(() => {
    getSegmentsData(datasetIdParam);
    getStoriesData(datasetIdParam);
  }, [datasetIdParam, tenant, getStoriesData, getSegmentsData]);

  return (
    <Presentation
      story={stories.length > 0 ? stories[0] : null}
      segments={segments}
      hasData={stories.length > 0 && segments.length > 0}
      apiStatus={fetchStoriesStatus}
      currentSlide={currentSlide}
      hasNextSlide={hasNextSlide}
      hasPrevSlide={hasPrevSlide}
      onNext={() => {
        nextSlide();
      }}
      onPrev={() => {
        prevSlide();
      }}
      onClose={() => {
        history.length ? history.goBack() : history.push('/');
      }}
    />
  );
}

const mapStateToProps = ({ app, story }) => ({
  app,
  story,
  hasNextSlide: hasNextSlideSelector(story),
  hasPrevSlide: hasPrevSlideSelector(story),
});

const mapDispatchToProps = (dispatch) => ({
  nextSlide: () => dispatch(storyActions.nextSlide()),
  prevSlide: () => dispatch(storyActions.prevSlide()),
  fetchSegments: (query) => dispatch(storyActions.fetchSegments(query)),
  fetchStories: (query) => dispatch(storyActions.fetchStories(query)),
});

Story.propTypes = {
  app: PropTypes.shape({
    tenant: PropTypes.string.isRequired,
  }).isRequired,
  story: PropTypes.shape({
    stories: PropTypes.arrayOf(
      PropTypes.shape({
        slides: PropTypes.arrayOf(
          PropTypes.shape({
            view: PropTypes.string,
            segments: PropTypes.shape({}),
          })
        ),
      })
    ),
    segments: PropTypes.arrayOf(
      PropTypes.shape({
        pxScores: PropTypes.arrayOf(PropTypes.shape({})),
        dataset: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ).isRequired,
    currentSlide: PropTypes.number.isRequired,
    fetchStoriesStatus: PropTypes.oneOf([
      CONSTANTS.API.STARTED,
      CONSTANTS.API.SUCCEEDED,
      CONSTANTS.API.FAILED,
      undefined,
    ]).isRequired,
  }).isRequired,
  hasNextSlide: PropTypes.func.isRequired,
  hasPrevSlide: PropTypes.func.isRequired,
  nextSlide: PropTypes.func.isRequired,
  prevSlide: PropTypes.func.isRequired,
  fetchSegments: PropTypes.func.isRequired,
  fetchStories: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(Story));
